import React, { useState, useEffect, useCallback } from 'react'
import { opcionesDeporte } from '../../../lib/deportesOrg'
import { useNavigate } from 'react-router-dom'
import { Search, Truck, Plus, Package, Filter } from 'lucide-react'
import { FeaturedLayout } from '../../layouts'
import {
  Button, Badge, Card, CardContent, Input, Label,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  Tabs, TabsList, TabsTrigger,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, ChipFilter,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '../../ui'
import { getPedidos, crearPedido, agregarAPedido, asignarPrenda, actualizarAsignacion, addTemporadaPrenda } from '../../../lib/supabase'
import { useConfirmDialog } from '../../../hooks/useConfirmDialog'
import { useDeportes } from '../../../hooks/useDeportes'
import { useUnsavedGuard } from '../../../hooks/useUnsavedGuard'
import { formatDate } from '../../../utils/helpers'
import { useNecesidadMultiTemporada } from './pedido/useNecesidadMultiTemporada'
import { temporadaDelPedido, etiquetaTemporada } from '../../../lib/uniformesMultiTemporada'
import {
  estadoPedido, PEDIDO_ESTADO_LABEL, PEDIDO_ESTADO_BADGE,
} from './entrega/uniformesControlHelpers'
import { clasificarParaPedido } from '../../../lib/uniformesPedido'
import { jugadorEnGrupo } from './entrega/entregaHelpers'
import ListaNecesidad from './pedido/ListaNecesidad'

const todayISO = () => new Date().toISOString().slice(0, 10)

// Lo que el cobro hizo, en una frase. "Cubiertas por el plan" NO es un error: su costo
// ya va dentro del plan de esa familia, asi que cobrarlas aparte seria doble cobro.
const resumenCobro = (encabezado, cobro) => {
  const partes = [encabezado]
  if (!cobro) return partes.join(' ')
  if (cobro.creados) partes.push(`Se generaron ${cobro.creados} cobro(s) con su fecha.`)
  if (cobro.fijados) partes.push(`Se les puso fecha a ${cobro.fijados} cobro(s) que ya existían.`)
  if (cobro.cubiertasPorPlan) partes.push(`${cobro.cubiertasPorPlan} ya van dentro del plan de pagos: no se cobraron aparte.`)
  if (cobro.sinCobro) partes.push(`${cobro.sinCobro} no tienen precio ni concepto: no generan cobro.`)
  return partes.join(' ')
}

// Los cuatro estados del pedido, como datos. El orden es el del dueno: "se mandan a
// hacer todas las ya pagadas; de esas, cuales les faltan tallas; y de ahi se arma el
// pedido".
const ESTADOS = [
  {
    key: 'listas',
    label: 'Listas para pedir',
    corto: 'Listas',
    descripcion: 'El uniforme esta pagado y tiene talla. Esto es lo que puede irse al proveedor.',
    vacio: 'Ninguna prenda pagada y con talla todavia.',
  },
  {
    key: 'pagadasSinTalla',
    label: 'Falta la talla',
    corto: 'Falta talla',
    descripcion: 'Ya te pagaron y no puedes producir. Captura la talla aqui mismo.',
    vacio: 'Nadie que haya pagado esta esperando por su talla.',
    tono: 'atencion',
    capturable: true,
  },
  {
    key: 'sinPagar',
    label: 'Sin pagar',
    corto: 'Sin pagar',
    descripcion: 'Tienen su propio cobro y no lo han pagado. Puedes incluirlas si asi lo decides.',
    vacio: 'Nadie con cobro propio pendiente.',
    capturable: true,
  },
  {
    key: 'sinPagoPropio',
    label: 'Sin cobro propio',
    corto: 'Sin cobro',
    descripcion: 'No hay un cobro propio que consultar: o su costo va dentro del plan, o nadie se lo ha cobrado. No es lo mismo que no haber pagado.',
    vacio: 'Ninguna prenda en esta situacion.',
    capturable: true,
  },
]

const PedidosPage = () => {
  const navigate = useNavigate()
  const { alert, ConfirmDialog } = useConfirmDialog()
  const { label: deporteLabel } = useDeportes()
  // Varias temporadas a la vez: un pedido va a UN proveedor y puede llevar piezas
  // de FLAG y de TACKLE en la misma orden.
  const {
    temporadas, seleccionadas, setSeleccionadas,
    orgDeportes, grupos, matriz, loading: loadingMatriz, reload: reloadMatriz,
  } = useNecesidadMultiTemporada()

  // La vista ya no es una cinta de pestanas: se elige en Filtros.
  const [vista, setVista] = useState('por-pedir')
  const esPorPedir = vista === 'por-pedir'

  const jugadorById = Object.fromEntries((matriz.jugadores || []).map((j) => [j.id, j]))
  const prendaById = Object.fromEntries((matriz.prendas || []).map((p) => [p.id, p]))
  const deporteOptions = opcionesDeporte(orgDeportes)
  const deporteLabelById = Object.fromEntries(deporteOptions.map((d) => [d.value, d.label]))
  const temporadaById = Object.fromEntries(temporadas.map((t) => [t.id, t]))

  // ---- Por pedir ----
  // Que HAY QUE PEDIR: solo para quien participa este ciclo. El detalle de un
  // pedido ya hecho (mas abajo) SI usa la matriz completa: debe mostrar lo que se
  // ordeno, aunque ese jugador se haya dado de baja despues.
  //
  // El PAGO manda, no la talla. Antes esta pantalla agrupaba por prenda+talla y era
  // ciega al pago: la pantalla que decide que producir no mostraba lo unico que lo
  // decide. Y solo listaba lo que YA tenia talla, asi que con 0 asignaciones (el caso
  // real de RAIDERS) salia vacia sin decir por que.
  const bloques = clasificarParaPedido(matriz.entregas)

  const [grupoSel, setGrupoSel] = useState([])
  const [tallaSel, setTallaSel] = useState([])
  const [conTallaSel, setConTallaSel] = useState('todas')
  const [tipoSel, setTipoSel] = useState('todas')
  const [buscar, setBuscar] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [proveedor, setProveedor] = useState('')
  const [fecha, setFecha] = useState(todayISO())
  const [fechaPago, setFechaPago] = useState('')
  const [generando, setGenerando] = useState(false)

  // Ya NO se filtra por deporte: la temporada lo fija, y con la lista agrupada por
  // prenda el deporte se lee solo. Eran dos mandos para el mismo volante.
  const visiblesDeDeporte = (lista) => (lista || []).filter((e) => {
    const prenda = prendaById[e.prenda_id]
    if (!prenda) return false
    if (tallaSel.length && !tallaSel.includes(e.talla)) return false
    if (conTallaSel === 'con' && !e.talla) return false
    if (conTallaSel === 'sin' && e.talla) return false
    if (tipoSel === 'personalizada' && prenda.personalizada === false) return false
    if (tipoSel === 'generica' && prenda.personalizada !== false) return false
    if (grupoSel.length) {
      const jugador = jugadorById[e.jugador_id]
      if (!grupoSel.some((gid) => jugadorEnGrupo(jugador, gid))) return false
    }
    return true
  })
  const porEstado = {
    listas: visiblesDeDeporte(bloques.listas),
    pagadasSinTalla: visiblesDeDeporte(bloques.pagadasSinTalla),
    sinPagar: visiblesDeDeporte(bloques.sinPagar),
    sinPagoPropio: visiblesDeDeporte(bloques.sinPagoPropio),
  }
  const totalPendientes = ESTADOS.reduce((n, e) => n + porEstado[e.key].length, 0)

  // Arranca en el estado que SI tiene trabajo: caer siempre en "Listas para pedir"
  // con 0 hacia parecer que no hay nada que hacer.
  const [estadoManual, setEstadoManual] = useState(null)
  const estadoSel = estadoManual
    || (ESTADOS.find((e) => porEstado[e.key].length > 0)?.key ?? ESTADOS[0].key)
  const setEstadoSel = setEstadoManual
  const estadoActivo = ESTADOS.find((e) => e.key === estadoSel) || ESTADOS[0]
  const hayFiltrosActivos = grupoSel.length > 0 || tallaSel.length > 0
    || conTallaSel !== 'todas' || tipoSel !== 'todas'

  const limpiarFiltros = () => {
    setGrupoSel([])
    setTallaSel([])
    setConTallaSel('todas')
    setTipoSel('todas')
  }

  // Las opciones se calculan de lo que HAY, no de un catalogo fijo: un chip de una
  // talla que nadie pidio solo estorba.
  const gruposConGente = (grupos || [])
    .filter((g) => (matriz.jugadores || []).some((j) => jugadorEnGrupo(j, g.id)))
    .map((g) => ({ value: g.id, label: g.nombre_del_grupo }))
  const tallasDisponibles = [...new Set(
    ESTADOS.flatMap((e) => bloques[e.key]).map((x) => x.talla).filter(Boolean),
  )].sort().map((t) => ({ value: t, label: t }))

  // La busqueda es por jugador o por prenda: con 23 renglones de nombres, "¿ya le
  // capture la talla a Juan?" no se podia responder.
  const termino = buscar.trim().toLowerCase()
  const visibles = porEstado[estadoSel].filter((e) => {
    if (!termino) return true
    const jugador = jugadorById[e.jugador_id]?.nombre_completo || ''
    const prenda = prendaById[e.prenda_id]?.nombre || ''
    return jugador.toLowerCase().includes(termino) || prenda.toLowerCase().includes(termino)
  })

  // Se marca una LINEA de talla completa, no jugador por jugador: es la unidad que
  // el proveedor produce.
  const toggleId = (ids, marcar) => setSelectedIds((prev) => {
    const next = new Set(prev)
    ;(Array.isArray(ids) ? ids : [ids]).forEach((id) => {
      if (marcar) next.add(id); else next.delete(id)
    })
    return next
  })

  // Solo las que ya tienen registro pueden entrar a un pedido: el pedido cuelga de
  // jugadores_prendas.id. Las que solo son celda todavia no existen como fila.
  //
  // Se recorren TODOS los estados, no solo el visible: la seleccion sobrevive al
  // cambiar de segmento, asi se puede armar un pedido con piezas de varios.
  const todasLasVisibles = ESTADOS.flatMap((e) => porEstado[e.key])
  const idsSeleccionados = todasLasVisibles
    .filter((e) => selectedIds.has(e.id))
    .map((e) => e.asignacionId)
    .filter(Boolean)

  // Capturar la talla SIN salir de la pantalla. Antes habia que irse a "Tallas y
  // entrega", elegir la prenda y buscar al jugador; y desde Pedidos ni siquiera se
  // veia a quien le faltaba.
  // Guardar tarda, y hasta que termina la lista no cambia. Sin senal visible el
  // usuario vuelve a tocar -- de ahi salio el "duplicate key" que reporto el dueno.
  // El servicio ya es idempotente; esto ademas evita el segundo clic y lo dice.
  const [guardandoTalla, setGuardandoTalla] = useState(new Set())

  const capturarTalla = async (entrega, talla) => {
    if (guardandoTalla.has(entrega.id)) return
    setGuardandoTalla((prev) => new Set(prev).add(entrega.id))
    try {
      const res = entrega.asignacionId
        ? await actualizarAsignacion(entrega.asignacionId, { talla, estatus: entrega.estatus || 'pendiente' })
        : await asignarPrenda({
          jugadorId: entrega.jugador_id,
          prendaId: entrega.prenda_id,
          talla,
          // La temporada es la DE ESA PIEZA, no una global: con varias abiertas,
          // una global guardaria la talla en la temporada equivocada.
          temporadaId: entrega.temporada_id,
          origen: entrega.origen || 'plan',
          grupoId: entrega.grupo_id || null,
        })
      if (res.error) { await alert('Error', res.error.message || 'No se pudo guardar la talla'); return }
      await reloadMatriz()
    } finally {
      setGuardandoTalla((prev) => {
        const next = new Set(prev)
        next.delete(entrega.id)
        return next
      })
    }
  }

  // Una prenda puede aparecer sin ir en la temporada (tenia piezas de antes). El
  // dueno: "esto no deberia pasar... pero permiteme agregarlas a la temporada en
  // ese caso". Se arregla desde la misma tarjeta donde se nota.
  // Para dar de alta algo cuando no hay una pieza que lo decida, se usa la primera
  // temporada marcada. Con una sola marcada es la de siempre.
  const temporadaParaAlta = seleccionadas[0] || null

  // Se ofrecen las ABIERTAS (lo que el club tiene corriendo) mas cualquiera que
  // este marcada aunque ya no lo este, para no dejarla sin forma de desmarcarse.
  const chipsTemporada = (temporadas || [])
    .filter((t) => t.estado === 'abierta' || seleccionadas.includes(t.id))
    .map((t) => ({
      value: t.id,
      label: etiquetaTemporada(t, deporteLabel),
    }))

  const temporadaPrendaPorId = Object.fromEntries(
    (matriz.temporadaPrendas || []).map((tp) => [tp.prenda_id, tp.estatus]),
  )

  const agregarATemporada = async (prenda) => {
    const { error } = await addTemporadaPrenda({
      temporadaId: temporadaParaAlta, prendaId: prenda.id, deporte: prenda.deporte,
    })
    if (error) { await alert('Error', error.message || 'No se pudo agregar a la temporada'); return }
    reloadMatriz()
  }

  const limpiarSeleccion = () => {
    setSelectedIds(new Set())
    setProveedor('')
    setFecha(todayISO())
    setFechaPago('')
  }

  // Crear un pedido vacio y entrar a armarlo. Es la puerta que faltaba: antes solo
  // se podia crear seleccionando renglones que YA tuvieran talla, asi que un club
  // sin tallas capturadas no podia crear ninguno.
  const [nuevoModal, setNuevoModal] = useState(false)
  const nuevoGuard = useUnsavedGuard({ onClose: () => setNuevoModal(false) })

  const abrirNuevoPedido = () => {
    setProveedor('')
    setFecha(todayISO())
    setFechaPago('')
    setNuevoModal(true)
    nuevoGuard.resetDirty()
  }

  const crearPedidoVacio = async () => {
    if (!proveedor.trim() || !temporadaParaAlta) return
    setGenerando(true)
    const { data, error } = await crearPedido(
      { proveedor: proveedor.trim(), fecha, fechaPago: fechaPago || null, temporadaId: temporadaParaAlta },
      [],
    )
    setGenerando(false)
    if (error) { await alert('Error', error.message || 'No se pudo crear el pedido'); return }
    nuevoGuard.resetDirty()
    setNuevoModal(false)
    navigate(`/admin/uniformes/pedidos/${data.id}`)
  }

  const handleGenerarPedido = async () => {
    if (!proveedor.trim() || !idsSeleccionados.length) return
    setGenerando(true)
    // La temporada sale de las PIEZAS: si mezclan, ninguna (el campo es opcional y
    // escribir una de las dos seria mentir sobre el resto).
    const piezas = todasLasVisibles.filter((e) => selectedIds.has(e.id))
    const { data, cobro, error } = await crearPedido(
      {
        proveedor: proveedor.trim(), fecha, fechaPago: fechaPago || null,
        temporadaId: temporadaDelPedido(piezas) || temporadaParaAlta,
      },
      idsSeleccionados,
    )
    setGenerando(false)
    if (error) { await alert('Error', error.message || 'No se pudo generar el pedido'); return }
    await alert('Listo', resumenCobro(`Pedido creado en borrador con ${idsSeleccionados.length} prenda(s).`, cobro))
    navigate(`/admin/uniformes/pedidos/${data.id}`)
  }

  const handleAgregarABorrador = async (pedidoId) => {
    if (!pedidoId || !idsSeleccionados.length) return
    setGenerando(true)
    const { cobro, error } = await agregarAPedido(pedidoId, idsSeleccionados)
    setGenerando(false)
    if (error) { await alert('Error', error.message || 'No se pudo agregar al pedido'); return }
    await alert('Listo', resumenCobro(`Se agregaron ${idsSeleccionados.length} prenda(s) al pedido.`, cobro))
    navigate(`/admin/uniformes/pedidos/${pedidoId}`)
  }

  // ---- Pedidos ----
  const [pedidos, setPedidos] = useState([])
  const [loadingPedidos, setLoadingPedidos] = useState(true)
  const [searchPedidos, setSearchPedidos] = useState('')

  const reloadPedidos = useCallback(async () => {
    setLoadingPedidos(true)
    const { data } = await getPedidos()
    setPedidos(data || [])
    setLoadingPedidos(false)
  }, [])

  useEffect(() => { reloadPedidos() }, [reloadPedidos])

  const pedidosBorrador = pedidos.filter((p) => p.estado === 'borrador')
  const pedidosFiltrados = pedidos.filter((p) => !searchPedidos || (p.proveedor || '').toLowerCase().includes(searchPedidos.toLowerCase()))

  return (
    <FeaturedLayout
      title="Pedidos de uniformes"
      subtitle="Arma pedidos con lo pendiente y da seguimiento a su ciclo"
      refreshAction={async () => { await Promise.all([reloadMatriz(), reloadPedidos()]) }}
    >
      {/* Temporadas como chips que se marcan, no un desplegable.
          Un club puede tener FLAG y TACKLE corriendo a la vez y un pedido puede
          llevar de las dos. El desplegable obligaba a elegir UNA y escondia cual
          era la otra; ademas no mostraba el deporte, al punto de que en PROD el
          dueno acabo escribiendolo en el NOMBRE de la temporada ("FLAG - Julio -
          Diciembre 2026"). */}
      {chipsTemporada.length > 0 && (
        <div className="mb-3 space-y-1.5">
          <Label className="text-xs text-muted-foreground">Temporadas</Label>
          <ChipFilter
            ariaLabel="Temporadas"
            multiple
            scroll
            opciones={chipsTemporada}
            valor={seleccionadas}
            onChange={(ids) => { if (ids.length) setSeleccionadas(ids) }}
          />
        </div>
      )}

      {/* La vista (Por pedir / Pedidos) vive en Filtros, no como otra cinta de
          pestanas: el dueno pidio UNA sola cinta en pantalla, la de los estados. */}
      <div className="mb-4 flex items-center gap-2 flex-nowrap">
        <div className="relative min-w-0 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={esPorPedir ? 'Buscar jugador o prenda...' : 'Buscar proveedor...'}
            value={esPorPedir ? buscar : searchPedidos}
            onChange={(e) => (esPorPedir ? setBuscar(e.target.value) : setSearchPedidos(e.target.value))}
            className="pl-10"
          />
        </div>
        <span className="hidden whitespace-nowrap text-sm text-muted-foreground sm:block">
          <span className="font-medium text-foreground tabular-nums">
            {esPorPedir ? visibles.length : pedidosFiltrados.length}
          </span>
          {esPorPedir ? ' pendientes' : ' pedidos'}
        </span>
        <Button
          variant="outline"
          onClick={() => setShowFilters(!showFilters)}
          className="relative h-10 w-10 shrink-0 p-0 sm:h-10 sm:w-auto sm:px-3"
        >
          <Filter className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Filtros</span>
          {hayFiltrosActivos && <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-primary" />}
        </Button>
        <Button
          onClick={abrirNuevoPedido}
          disabled={!temporadaParaAlta}
          className="h-10 shrink-0"
          aria-label="Nuevo pedido"
          title="Nuevo pedido"
        >
          <Plus className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Nuevo pedido</span>
        </Button>
      </div>

              {showFilters && (
                <Card className="mb-4">
                  <CardContent className="space-y-4 pt-4">
                    {/* Chips y no selects: para listas cortas y conocidas, un select
                        esconde las opciones y su conteo detras de un clic. */}
                    <div className="space-y-1.5">
                      <Label className="text-xs">Ver</Label>
                      <ChipFilter
                        ariaLabel="Ver"
                        opciones={[
                          { value: 'por-pedir', label: 'Lo que hay por pedir' },
                          { value: 'pedidos', label: 'Los pedidos', count: pedidos.length },
                        ]}
                        valor={vista}
                        onChange={(v) => v && setVista(v)}
                      />
                    </div>

                    {gruposConGente.length > 0 && (
                      <div className="space-y-1.5">
                        <Label className="text-xs">Grupo</Label>
                        <ChipFilter
                          ariaLabel="Grupo"
                          multiple
                          opciones={gruposConGente}
                          valor={grupoSel}
                          onChange={setGrupoSel}
                        />
                      </div>
                    )}

                    {tallasDisponibles.length > 0 && (
                      <div className="space-y-1.5">
                        <Label className="text-xs">Talla</Label>
                        <ChipFilter
                          ariaLabel="Talla"
                          multiple
                          opciones={tallasDisponibles}
                          valor={tallaSel}
                          onChange={setTallaSel}
                        />
                      </div>
                    )}

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label className="text-xs">Talla capturada</Label>
                        <ChipFilter
                          ariaLabel="Talla capturada"
                          opciones={[
                            { value: 'todas', label: 'Todas' },
                            { value: 'con', label: 'Ya tienen' },
                            { value: 'sin', label: 'Faltan' },
                          ]}
                          valor={conTallaSel}
                          onChange={(v) => setConTallaSel(v || 'todas')}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Tipo de prenda</Label>
                        <ChipFilter
                          ariaLabel="Tipo de prenda"
                          opciones={[
                            { value: 'todas', label: 'Todas' },
                            { value: 'personalizada', label: 'Llevan nombre' },
                            { value: 'generica', label: 'Intercambiables' },
                          ]}
                          valor={tipoSel}
                          onChange={(v) => setTipoSel(v || 'todas')}
                        />
                      </div>
                    </div>

                    {hayFiltrosActivos && (
                      <Button variant="ghost" size="sm" onClick={limpiarFiltros}>
                        Quitar todos los filtros
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )}

      {esPorPedir ? (
          loadingMatriz ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Cargando...</p>
          ) : (
            <>

              {totalPendientes === 0 ? (
                /* Ningun vacio sin salida: dice que falta y lleva ahi. */
                <Card>
                  <CardContent className="py-12 text-center text-muted-foreground">
                    <Package className="mx-auto mb-3 h-12 w-12 opacity-30" />
                    <p className="mb-1">No hay prendas pendientes en esta temporada.</p>
                    <p className="mb-4 text-xs">
                      Las prendas aparecen aquí cuando están en la temporada y hay
                      jugadores inscritos en su deporte.
                    </p>
                    <Button variant="outline" onClick={() => navigate('/admin/uniformes/temporada')}>
                      Ver prendas de la temporada
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* Los cuatro estados son un FILTRO, no cuatro secciones. Antes se
                      apilaban como acordeones del mismo peso: tres cajas con 0 y la
                      unica con contenido hasta abajo y cerrada. */}
                  {/* Los cuatro estados como pestanas, con su conteo a la vista.
                      Es lo que el dueno aprobo; lo que estaba mal era el resto de
                      la pantalla, no esto. */}
                  {/* En movil la cinta se desliza: cuatro estados con su conteo no
                      caben en 390px. Se oculta la barra de desplazamiento (se veia
                      tosca) y las etiquetas se acortan, que es lo que de verdad
                      hace que quepan. */}
                  <Tabs value={estadoSel} onValueChange={setEstadoSel}>
                    <TabsList className="w-full justify-start overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                      {ESTADOS.map((e) => (
                        <TabsTrigger key={e.key} value={e.key} className="shrink-0 px-2 sm:px-3">
                          <span className="sm:hidden">{e.corto || e.label}</span>
                          <span className="hidden sm:inline">{e.label}</span>
                          <Badge
                            variant={estadoSel === e.key ? 'default' : 'outline'}
                            className="ml-1.5 tabular-nums sm:ml-2"
                          >
                            {porEstado[e.key].length}
                          </Badge>
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </Tabs>

                  <p className={`mb-2 mt-3 text-xs ${estadoActivo.tono === 'atencion' ? 'text-amber-700' : 'text-muted-foreground'}`}>
                    {estadoActivo.descripcion}
                  </p>
                  <ListaNecesidad
                    entregas={visibles}
                    jugadorById={jugadorById}
                    prendaById={prendaById}
                    seleccion={selectedIds}
                    onToggle={toggleId}
                    onCapturarTalla={estadoActivo.capturable ? capturarTalla : undefined}
                    guardando={guardandoTalla}
                    temporadaPrendaPorId={temporadaPrendaPorId}
                    onAgregarATemporada={agregarATemporada}
                    vacioTexto={termino ? 'Ninguna prenda coincide con la busqueda.' : estadoActivo.vacio}
                  />

                  {idsSeleccionados.length > 0 && (
                    <Card className="sticky bottom-0 z-10 mt-3 shadow-lg">
                      <div className="flex flex-wrap items-end gap-3 px-4 py-3">
                        <span className="shrink-0 text-sm font-medium">
                          {idsSeleccionados.length} prenda{idsSeleccionados.length === 1 ? '' : 's'}
                        </span>
                        <div className="min-w-[10rem] flex-1 space-y-1">
                          <Label className="text-xs">Proveedor *</Label>
                          <Input value={proveedor} onChange={(ev) => setProveedor(ev.target.value)} placeholder="Nombre del proveedor" className="h-9" />
                        </div>
                        <div className="w-40 space-y-1">
                          <Label className="text-xs">Fecha del pedido</Label>
                          <Input type="date" value={fecha} onChange={(ev) => setFecha(ev.target.value)} className="h-9" />
                        </div>
                        {/* Sin esto el pedido nace sin fecha de pago y NO genera ningun
                            cobro, en silencio. Hay dos caminos para crear un pedido --
                            este y el modal de "Nuevo pedido" -- y los dos tienen que
                            pedirla. */}
                        <div className="w-40 space-y-1">
                          <Label className="text-xs">Fecha de pago</Label>
                          <Input type="date" value={fechaPago} onChange={(ev) => setFechaPago(ev.target.value)} className="h-9" />
                        </div>
                        {pedidosBorrador.length > 0 && (
                          <div className="w-56 space-y-1">
                            <Label className="text-xs">O agregar a un borrador</Label>
                            <Select value="" onValueChange={handleAgregarABorrador} disabled={generando}>
                              <SelectTrigger className="h-9"><SelectValue placeholder="Elegir borrador" /></SelectTrigger>
                              <SelectContent>
                                {pedidosBorrador.map((p) => (
                                  <SelectItem key={p.id} value={p.id}>{p.proveedor} · {formatDate(p.fecha_pedido)}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        <div className="flex shrink-0 gap-2">
                          <Button size="sm" variant="ghost" onClick={limpiarSeleccion} disabled={generando}>Cancelar</Button>
                          <Button size="sm" onClick={handleGenerarPedido} disabled={generando || !proveedor.trim()}>
                            {generando ? 'Creando...' : 'Crear pedido'}
                          </Button>
                        </div>
                      </div>
                    </Card>
                  )}
                </>
              )}
            </>
          )
      ) : (

          loadingPedidos ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Cargando...</p>
          ) : pedidosFiltrados.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <Truck className="mx-auto mb-3 h-12 w-12 opacity-30" />
                <p className="mb-1">{searchPedidos ? 'Ningún pedido coincide con la búsqueda.' : 'Aún no hay pedidos.'}</p>
                {!searchPedidos && (
                  <>
                    <p className="mb-4 text-xs">
                      Crea uno con el proveedor y ve agregándole prendas conforme
                      captures las tallas.
                    </p>
                    <Button onClick={abrirNuevoPedido}>
                      <Plus className="mr-2 h-4 w-4" /> Nuevo pedido
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              {/* Movil: el proveedor es texto libre */}
              <div className="sm:hidden divide-y px-4">
                {pedidosFiltrados.map((p) => {
                  const estado = estadoPedido(p, p.lineas)
                  const temporadaNombre = temporadaById[p.temporada_id]?.nombre
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => navigate(`/admin/uniformes/pedidos/${p.id}`)}
                      className="flex w-full items-start justify-between gap-3 py-3 text-left"
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{p.proveedor}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {formatDate(p.fecha_pedido)}
                          {temporadaNombre ? ` · ${temporadaNombre}` : ''}
                          {p.deporte ? ` · ${deporteLabelById[p.deporte] || p.deporte}` : ''}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {p.cantidad_recibida}/{p.cantidad_pedida} recibidas
                        </p>
                      </div>
                      <Badge variant={PEDIDO_ESTADO_BADGE[estado]} className="shrink-0">
                        {PEDIDO_ESTADO_LABEL[estado] || estado}
                      </Badge>
                    </button>
                  )
                })}
              </div>

              {/* Escritorio */}
              <div className="hidden sm:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Proveedor</TableHead>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Temporada</TableHead>
                      <TableHead>Deporte</TableHead>
                      <TableHead>Recibidos</TableHead>
                      <TableHead>Estatus</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pedidosFiltrados.map((p) => {
                      const estado = estadoPedido(p, p.lineas)
                      return (
                        <TableRow key={p.id} className="cursor-pointer" onClick={() => navigate(`/admin/uniformes/pedidos/${p.id}`)}>
                          <TableCell className="font-medium">{p.proveedor}</TableCell>
                          <TableCell>{formatDate(p.fecha_pedido)}</TableCell>
                          <TableCell>{temporadaById[p.temporada_id]?.nombre || '—'}</TableCell>
                          <TableCell>{p.deporte ? (deporteLabelById[p.deporte] || p.deporte) : 'Todos'}</TableCell>
                          <TableCell>{p.cantidad_recibida}/{p.cantidad_pedida}</TableCell>
                          <TableCell>
                            <Badge variant={PEDIDO_ESTADO_BADGE[estado]}>{PEDIDO_ESTADO_LABEL[estado] || estado}</Badge>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            </Card>
          )
      )}
      {/* Nuevo pedido: solo proveedor y fecha. Nace vacio y se arma desde su
          detalle, agregandole prendas conforme llegan las tallas. */}
      <Dialog open={nuevoModal} onOpenChange={nuevoGuard.onOpenChange}>
        <DialogContent {...nuevoGuard.dirtyProps} className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nuevo pedido</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs" htmlFor="nuevo-proveedor">Proveedor *</Label>
              <Input
                id="nuevo-proveedor"
                value={proveedor}
                onChange={(e) => setProveedor(e.target.value)}
                placeholder="Nombre del proveedor"
                className="h-9"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs" htmlFor="nuevo-fecha">Fecha</Label>
              <Input
                id="nuevo-fecha"
                type="date"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                className="h-9"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs" htmlFor="nuevo-fecha-pago">Fecha de pago</Label>
              <Input
                id="nuevo-fecha-pago"
                type="date"
                value={fechaPago}
                onChange={(e) => { setFechaPago(e.target.value); nuevoGuard.markDirty() }}
                className="h-9"
              />
              <p className="text-[11px] text-muted-foreground">
                Cuándo vencen los cobros de estas prendas. Las piezas que le agregues
                toman esta fecha; si alguna todavía no tiene cobro, se le genera.
              </p>
            </div>
            <p className="text-xs text-muted-foreground">
              El pedido nace en borrador y vacío. Le agregas prendas desde "Por pedir"
              conforme captures las tallas, y lo marcas listo cuando esté completo.
              Puedes dejar la fecha de pago en blanco y ponerla después.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => nuevoGuard.onOpenChange(false)} disabled={generando}>
              Cancelar
            </Button>
            <Button onClick={crearPedidoVacio} disabled={generando || !proveedor.trim() || !temporadaParaAlta}>
              {generando ? 'Creando...' : 'Crear pedido'}
            </Button>
          </DialogFooter>
          {nuevoGuard.DiscardDialog}
        </DialogContent>
      </Dialog>

      <ConfirmDialog />
    </FeaturedLayout>
  )
}

export default PedidosPage
